import { api, ApiTags } from './base';
import { FINISH_REGISTRATION_BY_REPRESENTATIVE } from './mutations/organizations/finish.registration.by.representative';
import { CORE_ORGANIZATIONS_PREPARE } from './mutations/organizations/prepare.organization';
import { START_REGISTRATION_BY_REPRESENTATIVE } from './mutations/organizations/start.registration.by.representative';
import {
  CoreOrganizationsUpdateInput,
  CoreOrganizationsUpdateMyResponse,
  FinishRegistrationByRepresentativeInput,
  PrepareOrganizationInput,
  StartRegistrationByRepresentativeInput,
  StartRegistrationByRepresentativeResponse,
} from './mutations/organizations/types';
import { UPDATE_MY_ORGANIZATION } from './mutations/organizations/update.my.organizations';
import { GET_CORE_ORGANIZATIONS } from './queries/organizations/get.core.organizations';
import { GET_MY_ORGANIZATIONS } from './queries/organizations/get.my.organizations';
import { GET_ORGANIZATION } from './queries/organizations/get.organization';
import { GET_PARTNER_ORGANIZATION } from './queries/organizations/get.partner.organization';
import { GET_PARTNERS_ORGANIZATIONS } from './queries/organizations/get.partners.organizations';
import {
  CoreOrganizationsByIdResponse,
  CoreOrganizationsMyResponse,
  CoreOrganizationsParticipationsResponse,
  CoreOrganizationsPartnersResponse,
  CoreOrganizationsPrepareResponse,
  CoreOrganizationsQueryRequest,
  CoreOrganizationsQueryResponse,
  PartnerRequest,
  PartnersRequest,
} from './queries/organizations/types';
import { CoreOrganizationsPartnerResponse } from './queries/payments/types';
import { CoreWidgetsQueryResponse } from './queries/widget/types';
import { Organization, OrganizationParticipation, Partner, CoreWidgetT } from './types';
import { GET_CORE_WIDGETS } from './queries/widget/get.core.widget';
import { GET_MY_ORGANIZATIONS_PARTICIPATIONS } from './queries/organizations/get.core.organizations.participations';

import {
  GetCoreOrganizationsParticipationsQuery,
  GetCoreOrganizationsQuery,
  GetCoreWidgetQuery,
  GetMyOrganizationsQuery,
  GetOrganizationQuery,
  GetPartnerOrganizationQuery,
  GetPartnersOrganizationsQuery,
  useGetCoreOrganizationsParticipationsQuery as useGeneratedParticipationsQuery,
  useGetCoreOrganizationsQuery as useGeneratedCoreOrganizationsQuery,
  useGetCoreWidgetQuery as useGeneratedCoreWidgetQuery,
  useGetMyOrganizationsQuery as useGeneratedMyOrganizationsQuery,
  useGetOrganizationQuery as useGeneratedOrganizationQuery,
  useGetPartnerOrganizationQuery as useGeneratedPartnerOrganizationQuery,
  useGetPartnersOrganizationsQuery as useGeneratedPartnersOrganizationsQuery,
  useLazyGetMyOrganizationsQuery as useGeneratedLazyMyOrganizationsQuery,
} from './generated';
import { unwrapLazyQuery, unwrapQuery } from './unwrap';


export type OrganizationsListPage = {
  organizations: Organization[];
  cursor: { remainsCount: number | null; cursorNextId: string | null } | null;
};

export const useGetCoreOrganizationsQuery = (
  {
    searchName,
    limit,
    cursorId,
  }: { searchName?: string | null; limit?: number; cursorId?: string },
  options?: Parameters<typeof useGeneratedCoreOrganizationsQuery>[1],
) =>
  unwrapQuery(
    useGeneratedCoreOrganizationsQuery(
      {
        searchText: searchName ?? null,
        ...(limit != null ? { limit } : {}),
        ...(cursorId ? { cursorId } : {}),
      },
      options,
    ),
    (raw: GetCoreOrganizationsQuery | undefined): OrganizationsListPage | undefined => {
      const query = raw?.coreOrganizations?.query;
      if (!query) return undefined;
      return {
        organizations: (query.organizations ?? []) as Organization[],
        cursor: query.cursor ?? null,
      };
    },
  );

export const useGetMyOrganizationsQuery = (
  ...args: Parameters<typeof useGeneratedMyOrganizationsQuery>
) =>
  unwrapQuery(
    useGeneratedMyOrganizationsQuery(...args),
    (raw: GetMyOrganizationsQuery | undefined) =>
      raw?.coreOrganizations?.my as Organization[] | undefined,
  );

export const useLazyGetMyOrganizationsQuery = (
  ...args: Parameters<typeof useGeneratedLazyMyOrganizationsQuery>
) =>
  unwrapLazyQuery(
    useGeneratedLazyMyOrganizationsQuery(...args),
    (raw: GetMyOrganizationsQuery | undefined) =>
      raw?.coreOrganizations?.my as Organization[] | undefined,
  );

export const useGetMyOrganizationByIdQuery = (
  { id }: { id: string },
  options?: Parameters<typeof useGeneratedOrganizationQuery>[1],
) =>
  unwrapQuery(
    useGeneratedOrganizationQuery({ ids: [id] }, options),
    (raw: GetOrganizationQuery | undefined) =>
      raw?.coreOrganizations?.get as Organization[] | undefined,
  );

export const useGetMyOrganizationParticipationsQuery = (
  ...args: Parameters<typeof useGeneratedParticipationsQuery>
) =>
  unwrapQuery(
    useGeneratedParticipationsQuery(...args),
    (raw: GetCoreOrganizationsParticipationsQuery | undefined) =>
      raw?.coreOrganizationParticipations?.my as OrganizationParticipation[] | undefined,
  );

export const useGetMyCoreWidgetQuery = (
  ...args: Parameters<typeof useGeneratedCoreWidgetQuery>
) =>
  unwrapQuery(
    useGeneratedCoreWidgetQuery(...args),
    (raw: GetCoreWidgetQuery | undefined) => (raw?.coreWidgets?.my ?? []) as CoreWidgetT[],
  );

export const useGetPartnerOrganizationQuery = (
  ...args: Parameters<typeof useGeneratedPartnerOrganizationQuery>
) =>
  unwrapQuery(
    useGeneratedPartnerOrganizationQuery(...args),
    (raw: GetPartnerOrganizationQuery | undefined) =>
      raw?.coreOrganizations?.getPartner as Partner | undefined,
  );

export const useGetPartnersOrganizationsQuery = (
  { searchText }: { searchText?: string | null },
  options?: Parameters<typeof useGeneratedPartnersOrganizationsQuery>[1],
) =>
  unwrapQuery(
    useGeneratedPartnersOrganizationsQuery({ searchText: searchText ?? null }, options),
    (raw: GetPartnersOrganizationsQuery | undefined) =>
      raw?.coreOrganizations?.getPartners?.organizations as Organization[] | undefined,
  );

export const organizationsApi = api.injectEndpoints({
  endpoints: (builder) => ({
    updateMyOrganization: builder.mutation<
      Organization,
      { input: CoreOrganizationsUpdateInput }
    >({
      query: ({ input }) => ({
        document: UPDATE_MY_ORGANIZATION,
        variables: { input },
      }),
      transformResponse: (rawResponse: CoreOrganizationsUpdateMyResponse) =>
        rawResponse?.coreOrganizations?.update,
      invalidatesTags: [ApiTags.Organization],
    }),

    prepareOrganization: builder.mutation<string, PrepareOrganizationInput>({
      query: ({ organizationAttributes }) => ({
        document: CORE_ORGANIZATIONS_PREPARE,
        variables: { organizationAttributes },
      }),
      transformResponse: (rawResponse: CoreOrganizationsPrepareResponse) =>
        rawResponse?.coreOrganizations?.prepare.organization.id,
    }),
    startRegistrationByRepresentative: builder.mutation<
      string,
      StartRegistrationByRepresentativeInput
    >({
      query: ({ inviteId, poaId }) => ({
        document: START_REGISTRATION_BY_REPRESENTATIVE,
        variables: {
          poaId,
          inviteId,
        },
      }),
      transformResponse: (rawResponse: StartRegistrationByRepresentativeResponse) =>
        rawResponse?.coreOrganizations?.startRegistrationByRepresentative?.joinDocumentContent,
    }),
    finishRegistrationByRepresentative: builder.mutation<
      void,
      FinishRegistrationByRepresentativeInput
    >({
      query: ({ inviteId, poaId, signature, detachedSignature }) => ({
        document: FINISH_REGISTRATION_BY_REPRESENTATIVE,
        variables: {
          poaId,
          inviteId,
          signature,
          detachedSignature,
        },
      }),
    }),
  }),
});

export const {
  usePrepareOrganizationMutation,
  useUpdateMyOrganizationMutation,
  useStartRegistrationByRepresentativeMutation,
  useFinishRegistrationByRepresentativeMutation,
} = organizationsApi;
